import React, { useState, useEffect, useCallback } from 'react';
import { Col, Row } from 'antd';
import HouseComment from './components/HouseComment';
import HouseBalance from './components/HouseBalance';
import HouseHead from './components/HouseHead';
import HouseDocuments from './components/HouseDocuments';
import HouseAbout from './components/HouseAbout';
import HouseStage from './components/HouseStage';
import HouseNotificationHistory from './components/HouseNotificationHistory';
import HouseRelatedObjects from './components/HouseRelatedObjects';
import HouseDownloadReports from './components/HouseDownloadReports';
import useCurrentAddress from '../../../../hooks/useCurrentAddress';
import useAwailableReports from '../../../../queries/useAwailableReports';
import useFlatsGridState from '../../../../hooks/useFlatsGridState';
import StatusBlock from '../../../../components/ui/StatusBlock/StatusBlock';
import { addressStagesColorsMap } from '../../../../constants/stagesColorMaps';
import { setCountMap } from './utils/setCountMap';
import { mapToSortArr } from './utils/mapToSortArr';
import HouseStagesDrawer from './components/HouseStagesDrawer/HouseStagesDrawer';
import styles from './HouseInfo.module.scss';
import { useAcpentanceQuery } from '../../../../hooks/useAccpentanceQuery';
import { useStagesQuery } from '../../../../hooks/useStageLifeCircleQuery';

const HouseInfo = () => {
  const address = useCurrentAddress();

  const { flatsGridState } = useFlatsGridState();

  const [flatStagesMap, setFlatStagesMap] = useState(new Map());
  const [acceptanceResultsMap, setAcceptanceResultsMap] = useState(new Map());
  const [statusesDrawerVisible, setStatusesDrawerVisible] = useState(false);

  const { data: awailableReports } = useAwailableReports({
    staleTime: Infinity,
  });

  const { data: flatStages } = useStagesQuery({
    staleTime: Infinity,
    select: (data) => data?.stagesAddressLifeCycle,
  });

  const { data: acceptanceResults } = useAcpentanceQuery({
    staleTime: Infinity,
    select: (data) => data?.acceptanceStatuses,
  });

  const hasBottomDrawerData = Boolean(
    flatStages?.length && acceptanceResults?.length
  );

  const handleStagesActionClick = useCallback(() => {
    setStatusesDrawerVisible(true);
  }, []);

  const handleStatusesDrawerClose = () => {
    setStatusesDrawerVisible(false);
  };

  useEffect(() => {
    let newFlatStagesMap = new Map();
    let newAcceptanceResultsMap = new Map();

    const { data: flats } = flatsGridState || {};

    if (Boolean(flats?.length)) {
      flats.forEach((flat) => {
        setCountMap(
          newFlatStagesMap,
          flat.addressLifeCycle?.stageAddressLifeCycle?.id,
          flat.addressLifeCycle?.stageAddressLifeCycle?.name
        );

        if (flat.addressLifeCycle?.acceptanceStatusAddressLifeCycle) {
          setCountMap(
            newAcceptanceResultsMap,
            flat.addressLifeCycle.acceptanceStatusAddressLifeCycle.id,
            flat.addressLifeCycle.acceptanceStatusAddressLifeCycle.name
          );
        }
      });
    }

    setFlatStagesMap(newFlatStagesMap);
    setAcceptanceResultsMap(newAcceptanceResultsMap);
  }, [flatsGridState]);

  return (
    <>
      <Row
        type="flex"
        className={styles.root}
        gutter={20}
      >
        <Col
          span={6}
          className={styles.col}
        >
          <HouseAbout address={address} />
          <HouseBalance address={address} />
          <HouseHead />
          <HouseComment address={address} />
        </Col>
        <Col
          className={styles.col}
          span={6}
        >
          <HouseStage />
          {flatStagesMap.size > 0 && (
            <StatusBlock
              statuses={mapToSortArr(flatStagesMap)}
              title="Статистика по стадиям помещения"
              onActionClick={
                hasBottomDrawerData ? handleStagesActionClick : undefined
              }
              indicatorBgColor={(id) => addressStagesColorsMap.get(id)}
            />
          )}
          {acceptanceResultsMap.size > 0 && (
            <StatusBlock
              statuses={mapToSortArr(acceptanceResultsMap)}
              title="Приемка"
              indicator={false}
            />
          )}
          <HouseNotificationHistory />
        </Col>
        <Col
          span={6}
          className={styles.col}
        >
          <HouseRelatedObjects />
        </Col>
        <Col
          span={6}
          className={styles.col}
        >
          <HouseDocuments />
          {Boolean(awailableReports?.length) && (
            <HouseDownloadReports awailableReports={awailableReports} />
          )}
        </Col>
      </Row>
      {hasBottomDrawerData && (
        <HouseStagesDrawer
          visible={statusesDrawerVisible}
          onClose={handleStatusesDrawerClose}
          acceptanceResults={acceptanceResults}
          acceptanceResultsMap={acceptanceResultsMap}
          flatStages={flatStages}
          flatStagesMap={flatStagesMap}
        />
      )}
    </>
  );
};

export default HouseInfo;
